import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, Chip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import MainTemplate from './components/MainTemplate/MainTemplate';
import Heading from './components/Heading/Heading';
import { paidCampusForChild } from './utils/paymentResume';
import { getChildCampusName } from './utils/parentHistoryHelpers';

/**
 * Your answers, read-only, for a family who has already paid one campus.
 *
 * Reached by "Back" from the payment page once a campus is paid. The earlier screens cannot be
 * reopened then: the discount answers apply to the whole family, so changing one would reprice a
 * payment already taken, and the forms behind them save as they open. This shows what was answered
 * — each child as paid, enrolling or leaving, and the discount answers — and changes nothing.
 */
const yearLabel = (year) => {
    const value = Number(year);
    if (value === -1) return 'Kindergarten';
    if (value === 0) return 'Pre-primary';
    return Number.isFinite(value) ? `Year ${value}` : '';
};

const Row = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid #e2e8f0' }}>
        <Typography sx={{ color: '#475569', fontSize: '14px' }}>{label}</Typography>
        <Typography sx={{ color: '#0f172a', fontSize: '14px', fontWeight: 600, textAlign: 'right' }}>{value || '—'}</Typography>
    </Box>
);

const YourAnswers = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const state = location.state || {};
    const paidCampuses = (state.campusStatuses || []).filter((campus) => campus?.paid);
    const enrolledCodes = new Set((state.enrolledchildrens || []).map((child) => String(child.stud_code)));
    const children = state.childrens?.length ? state.childrens : (state.enrolledchildrens || []);
    const isStaff = String(state.SM).toLowerCase() === 'yes';
    const hasCard = String(state.SelectedHCCValue).toLowerCase() === 'yes';

    const statusFor = (child) => {
        const paid = paidCampusForChild(child, paidCampuses);
        if (paid) return { label: `Paid — ${paid.college}${paid.paidOn || paid.t_date ? `, ${paid.paidOn || paid.t_date}` : ''}`, tone: 'paid' };
        const enrolled = child.enrolled !== undefined ? child.enrolled : enrolledCodes.has(String(child.stud_code));
        return enrolled ? { label: 'Enrolling', tone: 'enrolling' } : { label: 'Leaving AIC', tone: 'leaving' };
    };
    const toneSx = {
        paid: { bgcolor: '#d1fae5', color: '#065f46' },
        enrolling: { bgcolor: '#e0f2fe', color: '#075985' },
        leaving: { bgcolor: '#f1f5f9', color: '#475569' },
    };

    const backToPayment = () => navigate('/payment', { state });

    const content = (
        <Box sx={{ maxWidth: 720, mx: 'auto' }} data-testid="your-answers">
            <Button startIcon={<ArrowBackIcon />} onClick={backToPayment} sx={{ color: '#059669', fontWeight: 700, textTransform: 'none', mb: 1 }}>
                Back to payment
            </Button>
            <Heading text="Your answers" />
            <Alert severity="info" sx={{ my: 2, borderRadius: '12px' }}>
                {`You have already paid for ${paidCampuses.map((campus) => campus.college).join(' and ') || 'one campus'}, so these answers cannot be changed here. To change anything, please contact the Accounts team on (08) 9362 2100.`}
            </Alert>

            <Card sx={{ p: 2.5, mb: 2, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '16px', mb: 1 }}>Your children</Typography>
                {children.map((child) => {
                    const status = statusFor(child);
                    return (
                        <Box key={child.stud_code} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid #e2e8f0' }}>
                            <Box>
                                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                                    {[child.first_name || child.student_name, child.surname || child.student_surname].filter(Boolean).join(' ') || child.stud_code}
                                </Typography>
                                <Typography sx={{ fontSize: '12.5px', color: '#64748b' }}>
                                    {[getChildCampusName(child), yearLabel(child.year_grp) && `going into ${yearLabel(child.year_grp)}`].filter(Boolean).join(' · ')}
                                </Typography>
                            </Box>
                            <Chip size="small" label={status.label} sx={{ fontWeight: 700, ...toneSx[status.tone] }} />
                        </Box>
                    );
                })}
            </Card>

            <Card sx={{ p: 2.5, mb: 2, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '16px', mb: 1 }}>Discounts</Typography>
                <Row label="Discount" value={state.DiscountOption || 'No Discount'} />
                <Row label="Staff member" value={isStaff ? 'Yes' : 'No'} />
                {isStaff && <Row label="Paying the whole year now" value={String(state.SD).toLowerCase() === 'yes' ? 'Yes' : 'No'} />}
                <Row label="Health Care Card" value={hasCard ? `Yes${state.HCCExpiryDate && state.HCCExpiryDate !== 'No' ? `, expires ${state.HCCExpiryDate}` : ''}` : 'No'} />
            </Card>

            <Button variant="contained" size="large" fullWidth onClick={backToPayment} sx={{ bgcolor: '#059669', fontWeight: 700, textTransform: 'none', borderRadius: '12px', '&:hover': { bgcolor: '#047857' } }}>
                Back to payment
            </Button>
        </Box>
    );

    return <MainTemplate content={content} college={state.college} SessionID={state.SessionID} />;
};

export default YourAnswers;
